'use client';

import Image, { type ImageProps } from 'next/image';
import { useState } from 'react';

type ThumbImageProps = Omit<ImageProps, 'src'> & {
  /** Pre-generated thumbnail URL (e.g. `-thumb.webp` or `-card.webp`). */
  thumbSrc: string;
  /** Full-resolution original URL — rendered if the thumbnail 404s. */
  fallbackSrc: string;
};

/**
 * Renders the pre-generated thumbnail variant of a Supabase Storage image and
 * falls back to the original on a 404. The fallback path catches images
 * uploaded before the thumbnail pipeline existed (and any case where the
 * backfill script hasn't reached an image yet). Once
 * `scripts/backfill-image-thumbnails.ts` has run across the library the
 * fallback becomes unreachable.
 *
 * `unoptimized` is set globally via `images.unoptimized: true` in
 * next.config, so this wrapper does NOT route through Vercel's image
 * optimizer — both URLs are served directly from Supabase Storage's CDN.
 */
export function ThumbImage({
  thumbSrc,
  fallbackSrc,
  ...rest
}: ThumbImageProps) {
  const [src, setSrc] = useState(thumbSrc);
  return (
    <Image
      {...rest}
      src={src}
      onError={() => {
        if (src !== fallbackSrc) setSrc(fallbackSrc);
      }}
    />
  );
}
